"use client";

import { useLayoutEffect, useRef, useState } from "react";

/** Solo dígitos: "1.270.000" -> "1270000". */
export const digitsOnly = (raw: string): string => raw.replace(/\D/g, "");

/** Agrupa miles al estilo argentino: "1270000" -> "1.270.000". */
export const groupThousands = (digits: string): string => {
  const clean = digits.replace(/^0+(?=\d)/, "");
  if (clean === "") return "";
  return clean.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

/** Parsea lo que escribió el usuario a un entero, tolerando puntos y espacios. */
export const parseMoney = (raw: string): number => {
  const digits = digitsOnly(raw);
  return digits === "" ? 0 : Number(digits);
};

interface MoneyInputProps {
  value: number;
  onChange: (v: number) => void;
  max?: number;
  className?: string;
  "aria-label"?: string;
}

/**
 * Input de pesos que muestra separadores de miles mientras se escribe.
 * Es un input de texto con teclado numérico: `type="number"` no admite
 * puntos de miles y por eso 1270000 se veía pegado. El cursor se mantiene
 * relativo a los dígitos que tiene a la derecha, así se puede editar en el
 * medio del número sin que salte al final.
 */
export default function MoneyInput({
  value,
  onChange,
  max,
  className,
  "aria-label": ariaLabel,
}: MoneyInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  // Dígitos a la derecha del cursor antes de reformatear; null = no tocar.
  const pendingCaret = useRef<number | null>(null);
  // Texto tal como lo ve el usuario. Vacío se permite mientras edita.
  const [text, setText] = useState(() =>
    value === 0 ? "0" : groupThousands(String(value)),
  );

  // Si el valor cambia desde afuera (slider), resincronizamos el texto.
  // Patrón "ajustar estado cuando cambia una prop" de React: se compara con
  // el último valor visto en estado, no en un ref, para no tocar refs en render.
  const [lastValue, setLastValue] = useState(value);
  if (lastValue !== value) {
    setLastValue(value);
    if (parseMoney(text) !== value) {
      setText(value === 0 ? "0" : groupThousands(String(value)));
    }
  }

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el || pendingCaret.current === null) return;
    const digitsRight = pendingCaret.current;
    pendingCaret.current = null;
    // Buscamos la posición donde quedan exactamente `digitsRight` dígitos a la derecha.
    let pos = el.value.length;
    let seen = 0;
    while (pos > 0 && seen < digitsRight) {
      pos -= 1;
      if (/\d/.test(el.value[pos])) seen += 1;
    }
    el.setSelectionRange(pos, pos);
  }, [text]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    const digitsRight = digitsOnly(raw.slice(caret)).length;

    let digits = digitsOnly(raw);
    if (max !== undefined && digits !== "" && Number(digits) > max) {
      digits = String(max);
    }
    const formatted = groupThousands(digits);
    pendingCaret.current = digitsRight;
    setText(formatted);
    const next = digits === "" ? 0 : Number(digits);
    setLastValue(next);
    onChange(next);
  };

  const handleBlur = () => {
    if (text === "") setText("0");
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      pattern="[0-9.]*"
      aria-label={ariaLabel}
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
    />
  );
}
